import { useState } from "react";
import { RefreshCw, PlusCircle, Orbit, Share2, LogIn, LogOut, ChevronDown } from "lucide-react";
import { trackEvent } from "../../lib/track";
import { useAuth } from "../auth/AuthContext";

const UserMenu = () => {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) {
    return (
      <button
        onClick={() => { trackEvent("signin_click"); login(); }}
        data-testid="google-signin-btn"
        className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-xs md:text-sm font-medium"
      >
        <LogIn className="w-4 h-4" />
        <span className="hidden sm:inline">Sign in</span>
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="user-menu-btn"
        className="flex items-center gap-2 px-2 py-1.5 border border-border rounded-lg hover:bg-muted transition-colors"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-6 h-6 rounded-full" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold">
            {(user.name || "U").charAt(0).toUpperCase()}
          </span>
        )}
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-xl z-50 p-3" data-testid="user-menu-dropdown">
            <p className="text-sm font-semibold truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate mb-3">{user.email}</p>
            <button
              onClick={() => { setOpen(false); logout(); }}
              data-testid="logout-btn"
              className="w-full flex items-center gap-2 px-3 py-2 text-xs border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export const AppHeader = ({
  loading,
  fetchData,
  setActiveTab,
  setShowLanding,
  setShowCiteModal,
}) => {
  return (
    <header className="sticky top-0 z-30 glass border-b border-border/50 px-4 md:px-6 py-3 md:py-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg md:text-2xl font-bold truncate">AI Orientation Observatory</h1>
          <p className="text-muted-foreground text-xs md:text-sm hidden sm:block">Tracking the world's development in ethical AI</p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <button
            onClick={() => { sessionStorage.removeItem("aioo_entered"); setShowLanding(true); }}
            data-testid="homepage-btn"
            className="hidden sm:flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm font-medium"
          >
            <Orbit className="w-4 h-4" />
            Homepage
          </button>
          <button 
            onClick={fetchData}
            data-testid="refresh-btn"
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => { setShowCiteModal(true); trackEvent("cite_modal_open"); }}
            data-testid="share-cite-btn"
            className="hidden sm:flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm font-medium"
          >
            <Share2 className="w-4 h-4" />
            Share / Cite
          </button>
          <button 
            onClick={() => setActiveTab("contribute")}
            data-testid="submit-app-btn"
            className="flex items-center gap-2 px-3 md:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-xs md:text-sm"
          >
            <PlusCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Submit App</span>
            <span className="sm:hidden">Submit</span>
          </button>
          <UserMenu />
        </div>
      </div>
    </header>
  );
};
